import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { apiHelpers } from '../../services/api';
import ChartWidget from '../../components/charts/ChartWidget';
import InsightBlockWidget from '../../components/insights/InsightBlockWidget';
import ImageBlockWidget from '../../components/images/ImageBlockWidget';
import TextBlockWidget from '../../components/text/TextBlockWidget';
import PublicWatermarkBadge from '../../components/PublicWatermarkBadge';
import neoshiftLogo from '../../images/neoshiftBILogo.png';

const NEOSHIFT_URL = 'https://bi.neoshift.ai';

const ResponsiveGridLayout = WidthProvider(Responsive);

const STORAGE_KEY_PREFIX = 'neoshift-public-token-';

function formatTimeAgo(seconds) {
  if (seconds < 60) return 'just now';
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    return `${m} min${m !== 1 ? 's' : ''} ago`;
  }
  const h = Math.floor(seconds / 3600);
  return `${h} hr${h !== 1 ? 's' : ''} ago`;
}

export default function PublicDashboard() {
  const { slug } = useParams();
  const [token, setToken] = useState(() => sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${slug}`));
  const [dashboard, setDashboard] = useState(null);
  const [charts, setCharts] = useState([]);
  const [insightBlocks, setInsightBlocks] = useState([]);
  const [imageBlocks, setImageBlocks] = useState([]);
  const [textBlocks, setTextBlocks] = useState([]);
  const [layout, setLayout] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null);
  const cacheTimestampsRef = useRef({});

  // Fetch link info to determine if public access is available
  useEffect(() => {
    if (!slug) return;
    apiHelpers.getPublicDashboardInfo(slug)
      .then(res => setLinkInfo(res.data))
      .catch(() => setLinkInfo({ isPublicAccess: false }));
  }, [slug]);

  const loadDashboard = useCallback(async () => {
    if (!slug) return;
    if (!token && !linkInfo?.isPublicAccess) return;
    try {
      setIsLoading(true);
      const response = await apiHelpers.getPublicDashboard(slug, token || null);
      const { dashboard: d, charts: c, insightBlocks: ib, imageBlocks: imgb, textBlocks: tb } = response.data;
      setDashboard(d);
      setCharts(c || []);
      setInsightBlocks(ib || []);
      setImageBlocks(imgb || []);
      setTextBlocks(tb || []);
      setLayout(d?.layout || []);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${slug}`);
        setToken(null);
      }
      console.error('Failed to load public dashboard:', err);
    } finally {
      setIsLoading(false);
    }
  }, [slug, token, linkInfo?.isPublicAccess]);

  useEffect(() => {
    if (token || linkInfo?.isPublicAccess) {
      loadDashboard();
    } else if (linkInfo !== null) {
      setIsLoading(false);
    }
  }, [token, linkInfo, loadDashboard]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const form = e.target;
    const username = form.username?.value?.trim();
    const password = form.password?.value;
    if (!username || !password) {
      setLoginError('Please enter username and password');
      return;
    }
    setIsLoggingIn(true);
    setLoginError('');
    try {
      const response = await apiHelpers.publicDashboardLogin(slug, username, password);
      const { token: newToken } = response.data;
      sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${slug}`, newToken);
      setToken(newToken);
    } catch (err) {
      setLoginError(err.response?.data?.error || 'Invalid username or password');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${slug}`);
    setToken(null);
    setDashboard(null);
    setCharts([]);
    setInsightBlocks([]);
    setImageBlocks([]);
    setTextBlocks([]);
    setLayout([]);
  }, [slug]);

  const chartDataFetcher = useCallback(
    (chartId) => apiHelpers.getPublicChartData(slug, chartId, token || null).then((res) => {
      const cacheAge = res.data?.cacheAge ?? 0;
      cacheTimestampsRef.current[chartId] = {
        fetchedAt: Date.now(),
        cacheAge,
      };
      return res;
    }).catch((err) => {
      if (err.response?.status === 401 && token) {
        handleLogout();
      }
      throw err;
    }),
    [slug, token, handleLogout]
  );

  const filterOptionsFetcher = useCallback(
    (chartId, filterId) => apiHelpers.getPublicChartFilterOptions(slug, chartId, filterId, token || null),
    [slug, token]
  );

  useEffect(() => {
    if (!dashboard) return;

    const computeLabel = () => {
      const entries = Object.values(cacheTimestampsRef.current);
      if (entries.length === 0) return;
      let oldestAge = 0;
      for (const { fetchedAt, cacheAge } of entries) {
        const ageNow = cacheAge + (Date.now() - fetchedAt) / 1000;
        if (ageNow > oldestAge) oldestAge = ageNow;
      }
      setLastUpdatedLabel(formatTimeAgo(Math.round(oldestAge)));
    };

    computeLabel();
    const interval = setInterval(computeLabel, 30000);
    return () => clearInterval(interval);
  }, [dashboard, charts]);

  // No token and not a public link: show login form
  if (!token && !linkInfo?.isPublicAccess) {
    if (linkInfo === null) {
      return (
        <div className="min-h-screen bg-surface-50 dark:bg-surface-950 flex flex-col">
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-950 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm card p-8">
          <a href={NEOSHIFT_URL} target="_blank" rel="noopener noreferrer" className="flex justify-center mb-6">
            <img src={neoshiftLogo} alt="Neoshift BI" className="h-8" />
          </a>
          <h1 className="text-xl font-display font-bold text-surface-900 dark:text-white mb-2 text-center">
            Sign in to view dashboard
          </h1>
          <p className="text-surface-500 dark:text-surface-400 text-sm mb-6 text-center">
            Enter your credentials to access this shared dashboard.
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                className="input w-full"
                placeholder="Enter username"
                disabled={isLoggingIn}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                className="input w-full"
                placeholder="Enter password"
                disabled={isLoggingIn}
              />
            </div>
            {loginError && (
              <p className="text-red-500 text-sm">{loginError}</p>
            )}
            <button type="submit" className="btn-primary w-full" disabled={isLoggingIn}>
              {isLoggingIn ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
        <a href={NEOSHIFT_URL} target="_blank" rel="noopener noreferrer" className="mt-8 text-surface-400 dark:text-surface-500 text-sm hover:text-surface-600 dark:hover:text-surface-300 transition-colors">
          Powered by Neoshift BI
        </a>
      </div>
    );
  }

  // Loading dashboard
  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-950 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
        <footer className="py-4 text-center text-sm">
          <a href={NEOSHIFT_URL} target="_blank" rel="noopener noreferrer" className="text-surface-400 dark:text-surface-500 hover:text-surface-600 dark:hover:text-surface-300 transition-colors">
            Powered by Neoshift BI
          </a>
        </footer>
      </div>
    );
  }

  const insightBlockMap = Object.fromEntries(insightBlocks.map(ib => [ib._id.toString(), ib]));
  const imageBlockMap = Object.fromEntries(imageBlocks.map(ib => [ib._id.toString(), ib]));
  const textBlockMap = Object.fromEntries(textBlocks.map(tb => [tb._id.toString(), tb]));

  const gridLayout = layout
    .map(item => {
      let id;
      if (item.itemType === 'insightBlock') id = item.insightBlockId?.toString();
      else if (item.itemType === 'imageBlock') id = item.imageBlockId?.toString();
      else if (item.itemType === 'textBlock') id = item.textBlockId?.toString();
      else id = item.chartId?.toString();
      if (!id) return null;
      return { i: id, x: item.x, y: item.y, w: item.w, h: item.h, minW: 2, minH: 2 };
    })
    .filter(Boolean);

  const hasContent = charts.length > 0 || insightBlocks.length > 0 || imageBlocks.length > 0 || textBlocks.length > 0;

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950">
      <div className="max-w-[1600px] mx-auto px-6 pt-5 pb-16">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-display font-bold text-surface-900 dark:text-white truncate">
            {dashboard?.name || 'Dashboard'}
          </h1>
          <div className="flex items-center gap-4 flex-shrink-0">
            {lastUpdatedLabel && (
              <span className="text-xs text-surface-400 dark:text-surface-500 hidden sm:inline">
                Updated {lastUpdatedLabel}
              </span>
            )}
            {token && (
              <button
                onClick={handleLogout}
                className="text-sm text-surface-500 hover:text-surface-700 dark:text-surface-400 dark:hover:text-surface-200"
              >
                Sign out
              </button>
            )}
          </div>
        </div>

        {!hasContent ? (
          <div className="card p-12 text-center">
            <p className="text-surface-500 dark:text-surface-400">This dashboard has no content.</p>
          </div>
        ) : (
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: gridLayout }}
            breakpoints={{ lg: 768, md: 480, sm: 0 }}
            cols={{ lg: 12, md: 6, sm: 1 }}
            rowHeight={50}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            isDraggable={false}
            isResizable={false}
          >
            {layout.map(item => {
              if (item.itemType === 'insightBlock') {
                const ib = insightBlockMap[item.insightBlockId?.toString()];
                if (!ib) return null;
                return (
                  <div key={ib._id.toString()} className="card overflow-hidden h-full">
                    <InsightBlockWidget insightBlock={ib} readOnly={true} />
                  </div>
                );
              }
              if (item.itemType === 'imageBlock') {
                const img = imageBlockMap[item.imageBlockId?.toString()];
                if (!img) return null;
                return (
                  <div key={img._id.toString()} className="card overflow-hidden h-full">
                    <ImageBlockWidget imageBlock={img} readOnly={true} />
                  </div>
                );
              }
              if (item.itemType === 'textBlock') {
                const tb = textBlockMap[item.textBlockId?.toString()];
                if (!tb) return null;
                return (
                  <div key={tb._id.toString()} className="card overflow-hidden h-full">
                    <TextBlockWidget textBlock={tb} readOnly={true} />
                  </div>
                );
              }
              const chartId = item.chartId?.toString();
              const chart = charts.find(c => c._id.toString() === chartId);
              if (!chart) return null;
              return (
                <div key={chart._id.toString()} className="card overflow-hidden h-full">
                  <ChartWidget
                    chart={chart}
                    readOnly={true}
                    chartDataFetcher={chartDataFetcher}
                    filterOptionsFetcher={filterOptionsFetcher}
                  />
                </div>
              );
            }).filter(Boolean)}
          </ResponsiveGridLayout>
        )}
      </div>

      <PublicWatermarkBadge />
    </div>
  );
}
